// src/CustomMenu.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  Activity,
  BarChart2,
  Bell,
  Calendar,
  ClipboardList,
  FileText,
  Folder,
  Globe2,
  Handshake,
  Home,
  Hotel,
  Layers,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  MessagesSquare,
  Package,
  Phone,
  PhoneCall,
  QrCode,
  Scissors,
  Settings,
  ShoppingBag,
  Users,
  Wallet,
} from "lucide-react";

import {
  API_BASE,
  ORIGIN_BASE,
  UPLOAD_BASE,
} from "./config";

/* =========================================================
   DİL METİNLERİ
   ========================================================= */

const I18N = {
  tr: {
    home: "Ana Sayfa",
    menuManagement: "Menü Yönetimi",
    orders: "Siparişler",
    phoneOrder: "Telefon Siparişi",
    liveTables: "Masa Yönetimi",
    cashCollection: "Kasa & Tahsilat",
    cari: "Cari / Müşteriler",
    stockManagement: "Stok Yönetimi",
    marketplace: "Pazar Yeri / Mağaza",
    integrations: "Entegrasyonlar",
    fiscalPos: "Yazar Kasa POS",
   beauty: "Kuaför & Güzellik",
messageCenter: "Mesaj Merkezi",
hotel: "Otel Yönetimi",
mesyatPara: "MesYat Para Yönetimi",
coursePrograms: "Kurslar & Programlar",

businessNetwork: "Üye İş Ağı ve CRM",

    mainCategories: "Ana Kategoriler",
    homepageManagement: "Hakkımızda",
    activities: "Faaliyetler",
    announcements: "Duyurular",
    events: "Etkinlikler",
    documents: "Evraklarım",
    cemetery: "Mezarlıklar",
    appointments: "Randevular",
    accounting: "Muhasebe",
    digitalSignature: "Dijital İmza",
    contact: "İletişim",
    polls: "Anketler",
    feedback: "Geri Bildirim",
    qrCode: "QR Kod",
    franchiseSystem: "Bayilik Sistemi",
    users: "Kullanıcılar",
    settings: "Ayarlar",
    logout: "Çıkış",
  },

  en: {
    home: "Home",
    menuManagement: "Menu Management",
    orders: "Orders",
    phoneOrder: "Phone Order",
    liveTables: "Table Management",
    cashCollection: "Cash & Collections",
    cari: "Customers / Current",
    stockManagement: "Stock Management",
    marketplace: "Marketplace / Store",
    integrations: "Integrations",
    fiscalPos: "Fiscal POS",
   beauty: "Hairdresser & Beauty",

hotel: "Hotel Management",
mesyatPara: "MesYat Money Management",
coursePrograms: "Courses & Programs",

messageCenter: "Message Center",
businessNetwork: "Member Network & CRM",
    mainCategories: "Main Categories",
    homepageManagement: "About Us",
    activities: "Activities",
    announcements: "Announcements",
    events: "Events",
    documents: "My Documents",
    cemetery: "Cemetery",
    appointments: "Appointments",
    accounting: "Accounting",
    digitalSignature: "Digital Signature",
    contact: "Contact",
    polls: "Polls",
    feedback: "Feedback",
    qrCode: "QR Code",
    franchiseSystem: "Partner System",
    users: "Users",
    settings: "Settings",
    logout: "Logout",
  },

  de: {
    home: "Startseite",
    menuManagement: "Menüverwaltung",
    orders: "Bestellungen",
    phoneOrder: "Telefonbestellung",
    liveTables: "Tischverwaltung",
    cashCollection: "Kasse & Zahlungen",
    cari: "Kunden / Debitoren",
    stockManagement: "Lagerverwaltung",
    marketplace: "Marktplatz / Shop",
    integrations: "Integrationen",
    fiscalPos: "Kassen-POS",

  beauty: "Friseur & Schönheit",
messageCenter: "Nachrichtenzentrale",
hotel: "Hotelverwaltung",
mesyatPara: "MesYat-Geldverwaltung",
coursePrograms: "Kurse & Programme",

businessNetwork: "Mitgliedernetzwerk & CRM",

    mainCategories: "Hauptkategorien",
    homepageManagement: "Über uns",
    activities: "Aktivitäten",
    announcements: "Mitteilungen",
    events: "Veranstaltungen",
    documents: "Meine Dokumente",
    cemetery: "Friedhof",
    appointments: "Termine",
    accounting: "Buchhaltung",
    digitalSignature: "Digitale Signatur",
    contact: "Kontakt",
    polls: "Umfragen",
    feedback: "Rückmeldung",
    qrCode: "QR Code",
    franchiseSystem: "Partnersystem",
    users: "Benutzer",
    settings: "Einstellungen",
    logout: "Abmelden",
  },
};

function getLang() {
  const appLanguage = (
    localStorage.getItem("app_lang") || ""
  ).toLowerCase();

  const legacyLanguage = (
    localStorage.getItem("lang") || ""
  ).toLowerCase();

  const selectedLanguage =
    appLanguage ||
    legacyLanguage ||
    "tr";

  return ["tr", "en", "de"].includes(
    selectedLanguage
  )
    ? selectedLanguage
    : "tr";
}

function translate(
  language,
  key,
  fallback
) {
  return (
    I18N[language]?.[key] ??
    fallback ??
    key
  );
}

/* =========================================================
   LOGO VE UPLOAD URL
   ========================================================= */

const ORIGIN =
  ORIGIN_BASE ||
  (
    typeof window !== "undefined"
      ? window.location.origin
      : ""
  );

const UPLOAD_ROOT =
  UPLOAD_BASE &&
  UPLOAD_BASE.trim()
    ? UPLOAD_BASE.trim()
    : "";

function originWithoutPanel(
  originValue
) {
  try {
    const url = new URL(originValue);

    if (
      url.hostname.startsWith(
        "panel."
      )
    ) {
      url.hostname =
        url.hostname.replace(
          /^panel\./,
          ""
        );
    }

    return url.origin;
  } catch {
    return originValue;
  }
}

function normalizeUploadUrl(
  rawValue
) {
  if (!rawValue) return "";

  let value = String(rawValue)
    .trim()
    .replace(
      /^http:\/\//i,
      "https://"
    );

  if (
    !/^https?:\/\//i.test(value) &&
    !value.startsWith("/")
  ) {
    value = `/uploads/${value}`;
  }

  const stripUploadFolder = (
    pathname
  ) =>
    pathname
      .replace(
        /^\/?(upload|uploads)\//i,
        ""
      )
      .replace(/^\/+/, "");

  try {
    const absoluteUrl = new URL(
      value,
      ORIGIN
    );

    const filePart =
      stripUploadFolder(
        absoluteUrl.pathname
      );

    const selectedOrigin =
      UPLOAD_ROOT
        ? new URL(
            UPLOAD_ROOT,
            ORIGIN
          ).origin
        : originWithoutPanel(
            ORIGIN
          );

    const baseUrl = new URL(
      UPLOAD_ROOT || "/uploads",
      selectedOrigin
    );

    let basePath =
      baseUrl.pathname.replace(
        /\/+$/,
        ""
      );

    if (
      !/(\/upload(s)?)$/i.test(
        basePath
      )
    ) {
      basePath += "/uploads";
    }

    return `${baseUrl.origin}${basePath}/${filePart}`;
  } catch {
    return value;
  }
}

function addCacheVersion(
  url,
  version
) {
  if (!url) return "";

  return `${url}${
    url.includes("?")
      ? "&"
      : "?"
  }v=${version}`;
}

/* =========================================================
   PANEL MENÜ GÖRÜNÜRLÜĞÜ
   AyarlarPage.js anahtarlarıyla aynıdır.
   ========================================================= */

const PANEL_MENU_KEYS = [
  "home",
  "menuManagement",
  "orders",
  "TelefonSiparis",
  "liveTables",
  "cashCollection",
  "cari",
  "stockManagement",
  "marketplace",
  "integrations",
  "fiscalPos",
  "beauty",

"messageCenter",
"hotel",
"mesyatPara",
"coursePrograms",
"businessNetwork",


  "mainCategories",
  "homepageManagement",
  "activities",
  "announcements",
  "events",
  "documents",
  "cemetery",
  "appointments",
  "accounting",
  "digitalSignature",
  "contact",
  "polls",
  "feedback",
  "qrCode",
  "franchiseSystem",
  "users",
];

const DEFAULT_VISIBLE_MENU_ITEMS = [
  ...PANEL_MENU_KEYS,
];

const BUSINESS_NETWORK_MENU_MIGRATION_KEY =
  "app_business_network_menu_migrated_v2";

const PHONE_ORDER_MENU_MIGRATION_KEY =
  "app_phone_order_menu_migrated_v1";

const VISIBLE_MENU_USER_SAVED_KEY =
  "app_visible_menu_items_user_saved";

function normalizeVisibleMenuItems(
  rawValue,
  fallback =
    DEFAULT_VISIBLE_MENU_ITEMS
) {
  let value = rawValue;

  if (
    value === undefined ||
    value === null
  ) {
    value = [...fallback];
  }

  if (
    typeof value === "string"
  ) {
    const trimmedValue =
      value.trim();

    if (!trimmedValue) {
      value = [...fallback];
    } else {
      try {
        value =
          JSON.parse(
            trimmedValue
          );
      } catch {
        value = trimmedValue
          .split(",")
          .map((item) =>
            item.trim()
          )
          .filter(Boolean);
      }
    }
  }

  if (!Array.isArray(value)) {
    value = [...fallback];
  }

  const allowedKeys =
    new Set(PANEL_MENU_KEYS);

  const normalizedItems =
    value
      .map(String)
      .filter((key) =>
        allowedKeys.has(key)
      );

  const userSavedSelection =
    localStorage.getItem(
      VISIBLE_MENU_USER_SAVED_KEY
    ) === "true";

  const businessMigrationDone =
    localStorage.getItem(
      BUSINESS_NETWORK_MENU_MIGRATION_KEY
    ) === "true";

  const phoneMigrationDone =
    localStorage.getItem(
      PHONE_ORDER_MENU_MIGRATION_KEY
    ) === "true";

  /*
    Kullanıcı henüz panel menü
    seçimini kaydetmediyse eski
    kurumlara yeni menüler eklenir.
  */

  if (
    !userSavedSelection &&
    !businessMigrationDone &&
    !normalizedItems.includes(
      "businessNetwork"
    )
  ) {
    normalizedItems.push(
      "businessNetwork"
    );
  }

 

if (
  !userSavedSelection &&
  !phoneMigrationDone &&
  !normalizedItems.includes(
    "TelefonSiparis"
  )
) {
  normalizedItems.push(
    "TelefonSiparis"
  );
}

/*
  Kullanıcı daha önce özel menü
  seçimi kaydetmemişse yeni kurs
  modülü otomatik görünür.
*/
if (
  !userSavedSelection &&
  !normalizedItems.includes(
    "coursePrograms"
  )
) {
  normalizedItems.push(
    "coursePrograms"
  );
}

return [
  ...new Set(
    normalizedItems
  ),
];


}

function readVisibleMenuItemsFromStorage() {
  const storedValue =
    localStorage.getItem(
      "app_visible_menu_items"
    );

  const userSavedSelection =
    localStorage.getItem(
      VISIBLE_MENU_USER_SAVED_KEY
    ) === "true";

  return normalizeVisibleMenuItems(
    storedValue,
    userSavedSelection
      ? []
      : DEFAULT_VISIBLE_MENU_ITEMS
  );
}

/* =========================================================
   PERSONEL KULLANICI YETKİLERİ

   Kurum kullanıcısı:
   Kurumun açık bütün menülerini görür.

   Personel kullanıcısı:
   Kurumda açık menülerden sadece
   kendisine verilenleri görür.
   ========================================================= */

const STAFF_MENU_PERMISSIONS = {
  home: [
    "dashboard.view",
    "dashboard.stats",
    "dashboard.quick_actions",
  ],

  menuManagement: [
    "menu.view",
    "menu.manage",
    "menu.settings.view",
    "menu.settings.manage",
    "menu.products.view",
    "menu.products.manage",
    "menu.categories.view",
    "menu.categories.manage",
    "menu.prices.manage",
    "menu.images.manage",
  ],

  orders: [
    "orders.view",
    "orders.create",
    "orders.manage",
    "orders.delete",
    "orders.print",
  ],

  TelefonSiparis: [
    "phone_orders.view",
    "phone_orders.manage",
  ],

  liveTables: [
    "tables.view",
    "tables.manage",
    "waiter_call.view",
    "waiter_call.manage",
  ],

  cashCollection: [
    "cash.view",
    "cash.create",
    "cash.manage",
    "cash.delete",
    "cash.reports.view",
    "cash.export",

    /*
      Eski kayıtlarda bulunabilecek
      alternatif yetki isimleri.
    */
    "kasa.view",
    "kasa.manage",
  ],

  cari: [
    "current.view",
    "current.create",
    "current.manage",
    "current.delete",
    "current.payments.view",
    "current.payments.manage",
    "current.reports.view",
  ],

  stockManagement: [
    "stock.view",
    "stock.manage",
    "stock.cards.view",
    "stock.cards.manage",
    "stock.entry.manage",
    "stock.out.manage",
    "stock.count.manage",
    "stock.critical.view",
    "stock.movements.view",
    "stock.reports.view",
    "stock.settings.manage",

    /*
      Eski kayıtlarda bulunabilecek
      alternatif yetki isimleri.
    */
    "stok.view",
    "stok.manage",
  ],

  marketplace: [
    "marketplace.view",
    "marketplace.visibility.manage",
    "marketplace.profile.manage",
    "marketplace.listings.view",
    "marketplace.listings.manage",
  ],

  integrations: [
    "settings.view",
    "settings.manage",
    "system.admin_tools",
  ],

  fiscalPos: [
    "cash.view",
    "cash.manage",
    "system.admin_tools",
  ],

  beauty: [
    "beauty.view",
    "beauty.manage",
    "beauty.customers.view",
    "beauty.customers.manage",
    "beauty.services.view",
    "beauty.services.manage",
    "beauty.packages.view",
    "beauty.packages.manage",
    "beauty.sessions.view",
    "beauty.sessions.manage",
    "beauty.payments.view",
    "beauty.payments.manage",
    "beauty.reports.view",
  ],


hotel: [
  "hotel.view",
  "hotel.manage",
  "hotel.reservations.view",
  "hotel.reservations.manage",
  "hotel.checkin.manage",
  "hotel.checkout.manage",
  "hotel.rooms.view",
  "hotel.rooms.manage",
  "hotel.guests.view",
  "hotel.guests.manage",
  "hotel.housekeeping.view",
  "hotel.housekeeping.manage",
  "hotel.maintenance.view",
  "hotel.maintenance.manage",
  "hotel.finance.view",
  "hotel.finance.manage",
  "hotel.reports.view",
  "hotel.settings.manage",
],

// 💳 MesYat Para / MesPuan
mesyatPara: [
  "mespuan.view",
  "mespuan.manage",
],

// 🎓 Kurs, eğitim ve spor programları
coursePrograms: [



  "courses.view",
  "courses.manage",
],

messageCenter: [
    "messages.view",
    "messages.send",
    "messages.templates.view",
    "messages.templates.manage",
    "messages.automation.view",
    "messages.automation.manage",
    "messages.logs.view",
  ],

  businessNetwork: [
    "business_network.view",
    "business_network.profile.manage",
    "business_network.members.view",
    "business_network.members.manage",
    "business_network.jobs.view",
    "business_network.jobs.manage",
    "business_network.catalogs.manage",
    "business_network.settings.manage",
  ],

  mainCategories: [
    "menu.main_categories.view",
    "menu.main_categories.manage",
    "menu.view",
    "menu.manage",
  ],

  homepageManagement: [
    "company.info.manage",
    "news.view",
    "news.manage",
  ],

  activities: [
    "news.view",
    "news.manage",
  ],

  announcements: [
    "announcements.view",
    "announcements.manage",
  ],

  events: [
    "events.view",
    "events.manage",
  ],

  documents: [
    "files.view",
    "files.manage",
    "files.download",
  ],

  cemetery: [
    "memorial.view",
    "memorial.records.view",
    "memorial.records.manage",
    "memorial.gallery.manage",
    "memorial.condolences.view",
    "memorial.condolences.manage",
    "memorial.settings.manage",
  ],

  appointments: [
    "appointments.view",
    "appointments.create",
    "appointments.manage",
    "appointments.calendar.view",
    "appointments.settings.manage",
  ],

  accounting: [
    "accounting.view",
    "accounting.documents.view",
    "accounting.documents.manage",
    "accounting.tax.view",
    "accounting.tax.manage",
    "accounting.insurance.view",
    "accounting.insurance.manage",
  ],

  digitalSignature: [
    "digital_signature.view",
    "digital_signature.create",
    "digital_signature.manage",
    "digital_signature.archive.view",
    "digital_signature.settings.manage",
  ],

  contact: [
    "company.info.manage",
    "settings.view",
  ],

  polls: [
    "surveys.view",
    "surveys.manage",
    "forms.view",
    "forms.manage",
  ],

  feedback: [
    "feedback.view",
    "feedback.manage",
  ],

  qrCode: [
    "menu.view",
    "menu.manage",
    "settings.view",
  ],

  franchiseSystem: [
    "branches.view",
    "branches.manage",
    "branches.consolidated.view",
    "dealers.view",
    "dealers.manage",
    "dealers.commission.view",
    "dealers.commission.manage",
  ],

  users: [
    "users.manage",
  ],
};

const FULL_ACCESS_ROLES =
  new Set([
    "admin",
    "superadmin",
    "super_admin",
    "kurum",
    "institution",
    "firma_sahibi",
    "firma-sahibi",
    "company_owner",
    "owner",
  ]);

const KNOWN_STAFF_ROLES =
  new Set([
    "manager",
    "mudur",
    "müdür",
    "waiter",
    "garson",
    "cashier",
    "kasiyer",
    "stock_staff",
    "stok_sorumlusu",
    "menu_staff",
    "menu_sorumlusu",
    "content_staff",
    "icerik_sorumlusu",
    "accounting_staff",
    "muhasebe_sorumlusu",
    "beauty_staff",
    "personel",
    "staff",
    "employee",
    "readonly",
    "custom",
  ]);

function safeJsonParse(rawValue) {
  if (!rawValue) return null;

  if (
    typeof rawValue === "object"
  ) {
    return rawValue;
  }

  try {
    return JSON.parse(
      rawValue
    );
  } catch {
    return null;
  }
}

function decodeJwtPayload(token) {
  if (
    !token ||
    typeof token !== "string"
  ) {
    return null;
  }

  try {
    const tokenParts =
      token.split(".");

    if (
      tokenParts.length < 2
    ) {
      return null;
    }

    let base64 =
      tokenParts[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    while (
      base64.length % 4 !== 0
    ) {
      base64 += "=";
    }

    const binaryString =
      window.atob(base64);

    try {
      const bytes =
        Uint8Array.from(
          binaryString,
          (character) =>
            character.charCodeAt(0)
        );

      const jsonText =
        new TextDecoder(
          "utf-8"
        ).decode(bytes);

      return JSON.parse(
        jsonText
      );
    } catch {
      const decodedText =
        decodeURIComponent(
          Array.prototype.map
            .call(
              binaryString,
              (character) =>
                `%${(
                  "00" +
                  character
                    .charCodeAt(0)
                    .toString(16)
                ).slice(-2)}`
            )
            .join("")
        );

      return JSON.parse(
        decodedText
      );
    }
  } catch (error) {
    console.error(
      "Token bilgisi okunamadı:",
      error
    );

    return null;
  }
}

function permissionValueIsEnabled(
  value
) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "yes" ||
    value === "evet"
  );
}

function normalizePermissionSource(
  rawPermissions
) {
  if (
    rawPermissions === true
  ) {
    return true;
  }

  if (
    typeof rawPermissions ===
    "string"
  ) {
    const parsed =
      safeJsonParse(
        rawPermissions
      );

    if (parsed !== null) {
      return normalizePermissionSource(
        parsed
      );
    }

    /*
      Virgülle ayrılmış yetki listesi
      bulunursa onu da destekler.
    */

    const splitPermissions =
      rawPermissions
        .split(",")
        .map((item) =>
          item.trim()
        )
        .filter(Boolean);

    return splitPermissions.reduce(
      (result, permissionKey) => {
        result[permissionKey] =
          true;

        return result;
      },
      {}
    );
  }

  if (
    Array.isArray(
      rawPermissions
    )
  ) {
    return rawPermissions.reduce(
      (result, permissionKey) => {
        result[String(permissionKey)] =
          true;

        return result;
      },
      {}
    );
  }

  if (
    rawPermissions &&
    typeof rawPermissions ===
      "object"
  ) {
    /*
      Bazı yapılarda yetkiler
      permissions içinde tekrar olabilir.
    */

    if (
      rawPermissions.permissions &&
      rawPermissions.permissions !==
        rawPermissions
    ) {
      return normalizePermissionSource(
        rawPermissions.permissions
      );
    }

    if (
      rawPermissions.izinler &&
      rawPermissions.izinler !==
        rawPermissions
    ) {
      return normalizePermissionSource(
        rawPermissions.izinler
      );
    }

    return Object.entries(
      rawPermissions
    ).reduce(
      (
        result,
        [permissionKey, value]
      ) => {
        result[permissionKey] =
          permissionValueIsEnabled(
            value
          );

        return result;
      },
      {}
    );
  }

  return {};
}

function mergePermissionSources(
  ...permissionSources
) {
  let fullPermission = false;

  const mergedPermissions = {};

  permissionSources.forEach(
    (source) => {
      const normalizedSource =
        normalizePermissionSource(
          source
        );

      if (
        normalizedSource === true
      ) {
        fullPermission = true;
        return;
      }

      if (
        normalizedSource &&
        typeof normalizedSource ===
          "object"
      ) {
        Object.entries(
          normalizedSource
        ).forEach(
          (
            [
              permissionKey,
              permissionValue,
            ]
          ) => {
            if (
              permissionValueIsEnabled(
                permissionValue
              )
            ) {
              mergedPermissions[
                permissionKey
              ] = true;
            } else if (
              !Object.prototype.hasOwnProperty.call(
                mergedPermissions,
                permissionKey
              )
            ) {
              mergedPermissions[
                permissionKey
              ] = false;
            }
          }
        );
      }
    }
  );

  return fullPermission
    ? true
    : mergedPermissions;
}

function readStoredUserObjects() {
  const storageKeys = [
    "user",
    "auth_user",
    "current_user",
    "app_user",
    "logged_user",
    "personel_user",
    "staff_user",
    "user_info",
  ];

  return storageKeys
    .map((storageKey) =>
      safeJsonParse(
        localStorage.getItem(
          storageKey
        )
      )
    )
    .filter(
      (storedUser) =>
        storedUser &&
        typeof storedUser ===
          "object"
    );
}

function readCurrentPanelSession() {
  const token =
    localStorage.getItem(
      "token"
    ) || "";

  const tokenPayload =
    decodeJwtPayload(token) ||
    {};

  const storedUsers =
    readStoredUserObjects();

  const combinedSources = [
    tokenPayload,
    ...storedUsers,
  ];

  const roleSource =
    combinedSources.find(
      (source) =>
        source?.role ||
        source?.rol ||
        source?.user_role ||
        source?.userRole ||
        source?.staff_role ||
        source?.staffRole
    ) || {};

  const role = String(
    roleSource.role ||
      roleSource.rol ||
      roleSource.user_role ||
      roleSource.userRole ||
      roleSource.staff_role ||
      roleSource.staffRole ||
      ""
  )
    .trim()
    .toLowerCase();

  const explicitStaffSource =
    combinedSources.find(
      (source) =>
        source?.is_staff_user !==
          undefined ||
        source?.isStaffUser !==
          undefined ||
        source?.staff_user !==
          undefined ||
        source?.staffUser !==
          undefined ||
        source?.user_type !==
          undefined ||
        source?.userType !==
          undefined
    ) || {};

  const userType = String(
    explicitStaffSource.user_type ||
      explicitStaffSource.userType ||
      ""
  )
    .trim()
    .toLowerCase();

  const explicitlyStaff =
    explicitStaffSource.is_staff_user ===
      true ||
    explicitStaffSource.isStaffUser ===
      true ||
    explicitStaffSource.staff_user ===
      true ||
    explicitStaffSource.staffUser ===
      true ||
    userType === "staff" ||
    userType === "personel" ||
    userType === "employee";

  const inferredStaff =
    KNOWN_STAFF_ROLES.has(
      role
    );

  const permissions =
    mergePermissionSources(
      tokenPayload.permissions,
      tokenPayload.izinler,
      tokenPayload.authorizations,
      tokenPayload.yetkiler,
      tokenPayload.permission_map,

      localStorage.getItem(
        "permissions"
      ),

      localStorage.getItem(
        "user_permissions"
      ),

      localStorage.getItem(
        "app_permissions"
      ),

      ...storedUsers.flatMap(
        (storedUser) => [
          storedUser.permissions,
          storedUser.izinler,
          storedUser.authorizations,
          storedUser.yetkiler,
          storedUser.permission_map,
        ]
      )
    );

  return {
    role,

    isStaffUser:
      explicitlyStaff ||
      inferredStaff,

    permissions,
  };
}

function permissionIsEnabled(
  permissions,
  permissionKey
) {
  if (
    permissions === true
  ) {
    return true;
  }

  if (
    !permissions ||
    typeof permissions !==
      "object"
  ) {
    return false;
  }

  return permissionValueIsEnabled(
    permissions[
      permissionKey
    ]
  );
}

function staffCanSeeMenu(
  session,
  menuKey
) {
  /*
    Kurum, admin veya normal kurum
    hesabı personel filtresinden
    etkilenmez.
  */

  if (
    !session?.isStaffUser
  ) {
    return true;
  }

  if (
    FULL_ACCESS_ROLES.has(
      session.role
    )
  ) {
    return true;
  }

  const requiredPermissions =
    STAFF_MENU_PERMISSIONS[
      menuKey
    ];

  /*
    Personel için eşleştirilmemiş
    menüler güvenlik nedeniyle
    gösterilmez.
  */

  if (
    !Array.isArray(
      requiredPermissions
    ) ||
    requiredPermissions.length ===
      0
  ) {
    return false;
  }

  return requiredPermissions.some(
    (permissionKey) =>
      permissionIsEnabled(
        session.permissions,
        permissionKey
      )
  );
}

function staffCanSeeSettings(
  session
) {
  if (
    !session?.isStaffUser
  ) {
    return true;
  }

  if (
    FULL_ACCESS_ROLES.has(
      session.role
    )
  ) {
    return true;
  }

  return [
    "settings.view",
    "settings.manage",
    "users.manage",
  ].some(
    (permissionKey) =>
      permissionIsEnabled(
        session.permissions,
        permissionKey
      )
  );
}

/* =========================================================
   KURUM BİLGİLERİ
   ========================================================= */

function readBrandInfo(language) {
  let namesByLanguage = {};

  try {
    namesByLanguage =
      JSON.parse(
        localStorage.getItem(
          "app_institution_by_lang"
        ) || "{}"
      );
  } catch {
    namesByLanguage = {};
  }

  const institution =
    namesByLanguage?.[
      language
    ] ||
    namesByLanguage?.tr ||
    localStorage.getItem(
      "app_institution_name"
    ) ||
    "";

  const authorized =
    localStorage.getItem(
      "app_authorized_name"
    ) ||
    "Haymes Tasarım";

  const logo =
    normalizeUploadUrl(
      localStorage.getItem(
        "app_logo_url"
      ) ||
        "/logo192.png"
    );

  return {
    institution,
    authorized,
    logo,
  };
}

const SETTINGS_ENDPOINTS = [
  "kurum-ayarlar",
  "kurum-ayar",
  "ayarlar",
  "settings",
];

async function fetchSettingsFromApi(
  language
) {
  const token =
    localStorage.getItem(
      "token"
    );

  if (!token) return null;

  const endpointOrder = [
    localStorage.getItem(
      "app_settings_ep"
    ),
    ...SETTINGS_ENDPOINTS,
  ].filter(Boolean);

  const uniqueEndpoints = [
    ...new Set(
      endpointOrder
    ),
  ];

  for (
    const endpoint of uniqueEndpoints
  ) {
    try {
      const response =
        await fetch(
          `${API_BASE}/${endpoint}`,
          {
            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      if (!response.ok) {
        continue;
      }

      const json =
        await response
          .json()
          .catch(() => ({}));

      const data =
        json?.data ||
        json ||
        {};

      const namesByLanguage = {
        tr:
          data.firma_adi_tr ||
          "",

        en:
          data.firma_adi_en ||
          "",

        de:
          data.firma_adi_de ||
          "",
      };

      const institution =
        namesByLanguage[
          language
        ] ||
        namesByLanguage.tr ||
        namesByLanguage.en ||
        "";

      const authorized =
        data.yetkili_adi ||
        "";

      const logo =
        normalizeUploadUrl(
          data.logo_url ||
            data.logo ||
            ""
        );

      const serverVisibleMenuItems =
        normalizeVisibleMenuItems(
          data.visible_menu_items ??
            data.panel_visible_menu_items ??
            data.sidebar_menu_items
        );

      const localVisibleMenuRaw =
        localStorage.getItem(
          "app_visible_menu_items"
        );

      const userSavedSelection =
        localStorage.getItem(
          VISIBLE_MENU_USER_SAVED_KEY
        ) === "true";

      /*
        Kullanıcı Ayarlar sayfasında
        menü seçimini kaydetmişse API'nin
        eski menü listesi local seçimi
        ezemez.
      */

      const visibleMenuItems =
        userSavedSelection &&
        localVisibleMenuRaw !==
          null
          ? normalizeVisibleMenuItems(
              localVisibleMenuRaw,
              []
            )
          : serverVisibleMenuItems;

      localStorage.setItem(
        "app_visible_menu_items",
        JSON.stringify(
          visibleMenuItems
        )
      );

      localStorage.setItem(
        "app_institution_by_lang",
        JSON.stringify(
          namesByLanguage
        )
      );

      localStorage.setItem(
        "app_institution_name",
        institution
      );

      localStorage.setItem(
        "app_authorized_name",
        authorized
      );

      localStorage.setItem(
        "app_settings_ep",
        endpoint
      );

      if (logo) {
        localStorage.setItem(
          "app_logo_url",
          logo
        );
      }

      return {
        institution,
        authorized,
        logo,
        visibleMenuItems,
      };
    } catch {
      // Sonraki endpoint denenir.
    }
  }

  return null;
}

/* =========================================================
   MENÜ RENKLERİ
   ========================================================= */

const HIGHLIGHT_STYLES = {
  orange: {
    active:
      "from-orange-600 to-orange-700",

    hover:
      "hover:bg-orange-600/20",
  },

  green: {
    active:
      "from-green-600 to-green-700",

    hover:
      "hover:bg-green-600/20",
  },

  purple: {
    active:
      "from-purple-600 to-purple-700",

    hover:
      "hover:bg-purple-600/20",
  },

  blue: {
    active:
      "from-blue-600 to-blue-700",

    hover:
      "hover:bg-blue-600/20",
  },
};

/* =========================================================
   MENÜ TANIMLARI
   ========================================================= */

const MENU_DEFINITIONS = [
  {
    key: "home",
    path: "/",
    icon: Home,
    labelKey: "home",
    fallback: "Ana Sayfa",
  },

  {
    key: "menuManagement",
    path: "/menu-yonetimi",
    icon: ClipboardList,
    labelKey:
      "menuManagement",
    fallback:
      "Menü Yönetimi",
    highlight: "orange",
  },

  {
    key: "orders",
    path: "/siparisler",
    icon: FileText,
    labelKey: "orders",
    fallback: "Siparişler",
    highlight: "orange",
  },

  {
    key: "TelefonSiparis",
    path: "/telefon-siparis",
    icon: PhoneCall,
    labelKey: "phoneOrder",
    fallback:
      "Telefon Siparişi",
    highlight: "green",
  },

  {
    key: "liveTables",
    path: "/masa-yonetimi",
    icon: LayoutDashboard,
    labelKey: "liveTables",
    fallback:
      "Masa Yönetimi",
    highlight: "blue",
  },

  {
    key: "cashCollection",
    path: "/kasa-tahsilat",
    icon: Wallet,
    labelKey:
      "cashCollection",
    fallback:
      "Kasa & Tahsilat",
    highlight: "green",
  },

  {
    key: "cari",
    path: "/cari",
    icon: Users,
    labelKey: "cari",
    fallback:
      "Cari / Müşteriler",
    highlight: "green",
  },

  {
    key: "stockManagement",
    path: "/stok-takip",
    icon: Package,
    labelKey:
      "stockManagement",
    fallback:
      "Stok Yönetimi",
    highlight: "blue",
  },

  {
    key: "marketplace",
    path: "/marketplace",
    icon: ShoppingBag,
    labelKey: "marketplace",
    fallback:
      "Pazar Yeri / Mağaza",
    highlight: "orange",
  },

  {
    key: "integrations",
    path: "/entegrasyon",
    icon: Globe2,
    labelKey: "integrations",
    fallback:
      "Entegrasyonlar",
    highlight: "blue",
  },

  {
    key: "fiscalPos",
    path: "/yazarkasa",
    icon: Wallet,
    labelKey: "fiscalPos",
    fallback:
      "Yazar Kasa POS",
    highlight: "blue",
  },

  {
    key: "beauty",
    path: "/beaty-panel",
    icon: Scissors,
    labelKey: "beauty",
    fallback:
      "Kuaför & Güzellik",
    highlight: "purple",
  },


{
  key: "hotel",
  path: "/otel-yonetimi",
  icon: Hotel,
  labelKey: "hotel",
  fallback: "Otel Yönetimi",
  highlight: "orange",
},

{
  key: "mesyatPara",
  path: "/mesyat-para-yonetimi",
  icon: Wallet,
  labelKey: "mesyatPara",
  fallback: "MesYat Para Yönetimi",
  highlight: "blue",
},

{
  key: "coursePrograms",


  path: "/kurs-programlari",
  icon: ClipboardList,
  labelKey: "coursePrograms",
  fallback:
    "Kurslar & Programlar",
  highlight: "green",
},

{
  key: "messageCenter",


    path: "/mesaj-merkezi",
    icon: MessagesSquare,
    labelKey:
      "messageCenter",
    fallback:
      "Mesaj Merkezi",
    highlight: "purple",
  },

  {
    key: "businessNetwork",
    path: "/is-agi",
    icon: Handshake,
    labelKey:
      "businessNetwork",
    fallback:
      "Üye İş Ağı ve CRM",
    highlight: "green",
  },

  {
    key: "mainCategories",
    path: "/anakategoriler",
    icon: Layers,
    labelKey:
      "mainCategories",
    fallback:
      "Ana Kategoriler",
  },

  {
    key: "homepageManagement",
    path: "/bizkimiz",
    icon: LayoutDashboard,
    labelKey:
      "homepageManagement",
    fallback: "Hakkımızda",
  },

  {
    key: "activities",
    path: "/faaliyetler",
    icon: Activity,
    labelKey: "activities",
    fallback: "Faaliyetler",
  },

  {
    key: "announcements",
    path: "/duyurular",
    icon: Bell,
    labelKey:
      "announcements",
    fallback: "Duyurular",
  },

  {
    key: "events",
    path: "/etkinlikler",
    icon: Calendar,
    labelKey: "events",
    fallback: "Etkinlikler",
  },

  {
    key: "documents",
    path: "/evraklarim",
    icon: Folder,
    labelKey: "documents",
    fallback: "Evraklarım",
    highlight: "blue",
  },

  {
    key: "cemetery",
    path: "/mezarliklar",
    icon: FileText,
    labelKey: "cemetery",
    fallback: "Mezarlıklar",
    highlight: "orange",
  },

  {
    key: "appointments",
    path: "/randevular",
    icon: Calendar,
    labelKey:
      "appointments",
    fallback: "Randevular",
    highlight: "orange",
  },

  {
    key: "accounting",
    path: "/muhasebe",
    icon: BarChart2,
    labelKey: "accounting",
    fallback: "Muhasebe",
    highlight: "green",
  },

  {
    key: "digitalSignature",
    path: "/dijital-imza",
    icon: FileText,
    labelKey:
      "digitalSignature",
    fallback:
      "Dijital İmza",
    highlight: "purple",
  },

  {
    key: "contact",
    path: "/iletisim",
    icon: Phone,
    labelKey: "contact",
    fallback: "İletişim",
  },

  {
    key: "polls",
    path: "/anketler",
    icon: BarChart2,
    labelKey: "polls",
    fallback: "Anketler",
  },

  {
    key: "feedback",

    getPath: (language) =>
      `/geribildirimler?lang=${language}`,

    icon: MessageSquare,
    labelKey: "feedback",
    fallback:
      "Geri Bildirim",
  },

  {
    key: "qrCode",
    path: "/qr-kod",
    icon: QrCode,
    labelKey: "qrCode",
    fallback: "QR Kod",
    highlight: "orange",
  },

  {
    key: "franchiseSystem",
    path: "/bayilik-sistemi",
    icon: Handshake,
    labelKey:
      "franchiseSystem",
    fallback:
      "Bayilik Sistemi",
    highlight: "green",
  },

  {
    key: "users",
    path: "/kullanicilar",
    icon: Users,
    labelKey: "users",
    fallback: "Kullanıcılar",
  },
];

/* =========================================================
   CUSTOM MENU
   ========================================================= */

export default function CustomMenu() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const [
    language,
    setLanguage,
  ] = useState(
    getLang()
  );

  const [
    brand,
    setBrand,
  ] = useState(() =>
    readBrandInfo(
      getLang()
    )
  );

  const [
    logoVersion,
    setLogoVersion,
  ] = useState(0);

  const [
    visibleMenuItems,
    setVisibleMenuItems,
  ] = useState(
    readVisibleMenuItemsFromStorage
  );

  const [
    currentSession,
    setCurrentSession,
  ] = useState(
    readCurrentPanelSession
  );

  const lastTokenRef =
    useRef(
      localStorage.getItem(
        "token"
      ) || ""
    );

  /*
    LocalStorage üzerindeki kurum,
    menü ve kullanıcı yetkilerini
    anında yeniler.
  */

  const refreshFromLocalStorage =
    useCallback(() => {
      const currentLanguage =
        getLang();

      setLanguage(
        currentLanguage
      );

      setBrand(
        readBrandInfo(
          currentLanguage
        )
      );

      setVisibleMenuItems(
        readVisibleMenuItemsFromStorage()
      );

      setCurrentSession(
        readCurrentPanelSession()
      );

      setLogoVersion(
        (previousVersion) =>
          previousVersion + 1
      );
    }, []);

  /*
    Kurum bilgilerini API'den yükler.
    Personel yetkileri token veya
    localStorage üzerinden okunur.
  */

  const loadSettings =
    useCallback(
      async () => {
        const currentLanguage =
          getLang();

        const apiSettings =
          await fetchSettingsFromApi(
            currentLanguage
          );

        if (apiSettings) {
          const localBrand =
            readBrandInfo(
              currentLanguage
            );

          setLanguage(
            currentLanguage
          );

          setBrand({
            institution:
              apiSettings.institution ||
              localBrand.institution,

            authorized:
              apiSettings.authorized ||
              localBrand.authorized,

            logo:
              apiSettings.logo ||
              localBrand.logo,
          });

          setVisibleMenuItems(
            apiSettings.visibleMenuItems
          );

          setCurrentSession(
            readCurrentPanelSession()
          );

          setLogoVersion(
            (
              previousVersion
            ) =>
              previousVersion +
              1
          );
        } else {
          refreshFromLocalStorage();
        }

        lastTokenRef.current =
          localStorage.getItem(
            "token"
          ) || "";
      },
      [
        refreshFromLocalStorage,
      ]
    );

  useEffect(() => {
    loadSettings();

    const handleLanguageChange =
      () => {
        refreshFromLocalStorage();
      };

    const handleSettingsChange =
      () => {
        refreshFromLocalStorage();
      };

    const handlePermissionChange =
      () => {
        refreshFromLocalStorage();
      };

    const handleStorageChange = (
      event
    ) => {
      if (!event?.key) {
        return;
      }

      const watchedKeys = [
        "app_lang",
        "lang",
        "app_institution_by_lang",
        "app_institution_name",
        "app_authorized_name",
        "app_logo_url",
        "app_visible_menu_items",
        VISIBLE_MENU_USER_SAVED_KEY,
        "token",
        "permissions",
        "user_permissions",
        "app_permissions",
        "user",
        "auth_user",
        "current_user",
        "app_user",
        "logged_user",
        "personel_user",
        "staff_user",
        "user_info",
      ];

      if (
        !watchedKeys.includes(
          event.key
        )
      ) {
        return;
      }

      if (
        event.key === "token"
      ) {
        loadSettings();
        return;
      }

      refreshFromLocalStorage();
    };

    const handleFocus = () => {
      const currentToken =
        localStorage.getItem(
          "token"
        ) || "";

      if (
        currentToken !==
        lastTokenRef.current
      ) {
        loadSettings();
      } else {
        refreshFromLocalStorage();
      }
    };

    window.addEventListener(
      "app_lang_change",
      handleLanguageChange
    );

    window.addEventListener(
      "app_settings_change",
      handleSettingsChange
    );

    window.addEventListener(
      "app_permissions_change",
      handlePermissionChange
    );

    window.addEventListener(
      "storage",
      handleStorageChange
    );

    window.addEventListener(
      "focus",
      handleFocus
    );

    return () => {
      window.removeEventListener(
        "app_lang_change",
        handleLanguageChange
      );

      window.removeEventListener(
        "app_settings_change",
        handleSettingsChange
      );

      window.removeEventListener(
        "app_permissions_change",
        handlePermissionChange
      );

      window.removeEventListener(
        "storage",
        handleStorageChange
      );

      window.removeEventListener(
        "focus",
        handleFocus
      );
    };
  }, [
    loadSettings,
    refreshFromLocalStorage,
  ]);

  const isMenuVisible =
    useCallback(
      (menuKey) => {
        /*
          Bir menünün görünmesi için:

          1. Kurumun Panel Menü
             Görünürlüğünde açık olması.

          2. Giriş yapan kişi personelse
             ilgili yetkiye sahip olması.
        */

        const institutionMenuEnabled =
          visibleMenuItems.includes(
            menuKey
          );

        if (
          !institutionMenuEnabled
        ) {
          return false;
        }

        return staffCanSeeMenu(
          currentSession,
          menuKey
        );
      },
      [
        visibleMenuItems,
        currentSession,
      ]
    );

  const isActive =
    useCallback(
      (path) => {
        const pathname =
          String(
            path || ""
          ).split("?")[0];

        return (
          location.pathname ===
          pathname
        );
      },
      [
        location.pathname,
      ]
    );

  const handleLogout = () => {
    localStorage.removeItem(
      "token"
    );

    localStorage.removeItem(
      "app_institution_by_lang"
    );

    localStorage.removeItem(
      "app_institution_name"
    );

    localStorage.removeItem(
      "app_authorized_name"
    );

    localStorage.removeItem(
      "app_logo_url"
    );

    localStorage.removeItem(
      "app_settings_ep"
    );

    localStorage.removeItem(
      "app_visible_menu_items"
    );

    localStorage.removeItem(
      VISIBLE_MENU_USER_SAVED_KEY
    );

    localStorage.removeItem(
      BUSINESS_NETWORK_MENU_MIGRATION_KEY
    );

    localStorage.removeItem(
      PHONE_ORDER_MENU_MIGRATION_KEY
    );

    localStorage.removeItem(
      "app_business_network_menu_migrated"
    );

    /*
      Personel oturum bilgileri.
    */

    localStorage.removeItem(
      "permissions"
    );

    localStorage.removeItem(
      "user_permissions"
    );

    localStorage.removeItem(
      "app_permissions"
    );

    localStorage.removeItem(
      "user"
    );

    localStorage.removeItem(
      "auth_user"
    );

    localStorage.removeItem(
      "current_user"
    );

    localStorage.removeItem(
      "app_user"
    );

    localStorage.removeItem(
      "logged_user"
    );

    localStorage.removeItem(
      "personel_user"
    );

    localStorage.removeItem(
      "staff_user"
    );

    localStorage.removeItem(
      "user_info"
    );

    window.location.reload();
  };

  const logoSource =
    addCacheVersion(
      brand.logo ||
        "/logo192.png",

      logoVersion
    );

  const canSeeSettings =
    staffCanSeeSettings(
      currentSession
    );

  return (
    <aside
      className="
        fixed left-0 top-0 z-40
        flex h-[100dvh] max-h-[100dvh]
        w-[250px] flex-col overflow-hidden
        bg-gradient-to-b
        from-gray-800 to-gray-900
        text-white shadow-2xl
      "
    >
      {/* ÜST: LOGO VE KURUM BİLGİSİ */}

      <div
        className="
          relative
          border-b border-gray-700
          bg-gradient-to-r
          from-gray-900 to-gray-800
          px-4 pb-6 pt-8
        "
      >
        <div
          className="
            absolute left-0 top-0
            h-1 w-full
            bg-gradient-to-r
            from-orange-500
            via-purple-500
            to-blue-500
          "
        />

        <div className="flex flex-col items-center">
          <div className="relative">
            <img
              src={logoSource}
              alt="Logo"
              className="
                h-20 w-20 rounded-full
                border-4 border-gray-700
                bg-white object-cover
                shadow-xl
              "
              onError={(
                event
              ) => {
                event.currentTarget.src =
                  "/logo192.png";
              }}
            />

            <div
              className="
                absolute -bottom-1 -right-1
                h-5 w-5 rounded-full
                border-2 border-gray-800
                bg-green-500
              "
            />
          </div>

          <p
            className="
              mt-3 max-w-full truncate
              px-2 text-lg font-bold
              text-white
            "
          >
            {brand.institution}
          </p>

          <div
            className="
              mt-2 max-w-full rounded-full
              border border-gray-600
              bg-gray-700/50
              px-4 py-1.5
              backdrop-blur-sm
            "
          >
            <span
              className="
                block max-w-full truncate
                text-sm font-medium
                text-gray-300
              "
            >
              {brand.authorized}
            </span>
          </div>
        </div>
      </div>

      {/* ORTA: MENÜLER */}

      <nav
        className="
          mt-4 min-h-0 flex-1
          overflow-y-auto
          px-3 pb-4
          scrollbar-thin
          scrollbar-thumb-gray-600
        "
        style={{
          WebkitOverflowScrolling:
            "touch",
        }}
      >



        <ul className="space-y-1">
          {[
            {
              key: "home",
              title: null,
              menuKeys: [
                "home",
              ],
            },

            {
              key: "menuOrders",
              title: {
                tr: "Menü ve Sipariş Yönetimi",
                en: "Menu and Order Management",
                de: "Menü- und Bestellverwaltung",
              },
              menuKeys: [
                "menuManagement",
                "orders",
                "TelefonSiparis",
                "liveTables",
              ],
            },

            {
              key: "finance",
              title: {
                tr: "Finans ve Ticari İşlemler",
                en: "Finance and Commercial Operations",
                de: "Finanzen und Geschäftsprozesse",
              },
              menuKeys: [
                "cashCollection",
                "cari",
                "stockManagement",
                "accounting",
                "fiscalPos",
              ],
            },

            {
              key: "institutionContent",
              title: {
                tr: "Kurum Sayfası ve İçerikler",
                en: "Institution Page and Content",
                de: "Institutionsseite und Inhalte",
              },
              menuKeys: [
                "mainCategories",
                "homepageManagement",
                "activities",
                "announcements",
                "events",
                "contact",
                "documents",
                "cemetery",
                "qrCode",
              ],
            },

            {
              key: "customerCommunication",
              title: {
                tr: "Müşteri ve İletişim",
                en: "Customer and Communication",
                de: "Kunden und Kommunikation",
              },
              menuKeys: [
                "appointments",
                "messageCenter",
                "polls",
                "feedback",
                "digitalSignature",
              ],
            },

            {
              key: "sectorModules",
              title: {
                tr: "Sektörel Modüller",
                en: "Industry Modules",
                de: "Branchenmodule",
              },
              menuKeys: [
                "beauty",
                "hotel",
                "coursePrograms",
                "marketplace",
              ],
            },

            {
              key: "networkManagement",
              title: {
                tr: "İş Ağı ve Yönetim",
                en: "Business Network and Management",
                de: "Geschäftsnetzwerk und Verwaltung",
              },
              menuKeys: [
                "businessNetwork",
                "mesyatPara",
                "franchiseSystem",
                "integrations",
                "users",
              ],
            },
          ].map((group) => {
            const groupItems =
              group.menuKeys
                .map((menuKey) =>
                  MENU_DEFINITIONS.find(
                    (menuItem) =>
                      menuItem.key ===
                      menuKey
                  )
                )
                .filter(
                  (menuItem) =>
                    menuItem &&
                    isMenuVisible(
                      menuItem.key
                    )
                );

            if (
              groupItems.length === 0
            ) {
              return null;
            }

            return (
              <React.Fragment
                key={group.key}
              >
                {group.title && (
                  <li
                    className="
                      px-3 pb-1 pt-4
                    "
                  >
                    <div
                      className="
                        flex items-center
                        gap-2
                      "
                    >
                      <span
                        className="
                          shrink-0
                          text-[11px]
                          font-bold
                          tracking-wide
                          text-orange-400
                        "
                      >
                        {group.title[
                          language
                        ] ||
                          group.title.tr}
                      </span>

                      <span
                        className="
                          h-px min-w-4
                          flex-1
                          bg-orange-500/70
                        "
                      />
                    </div>
                  </li>
                )}

                {groupItems.map(
                  (menuItem) => {
                    const path =
                      menuItem.getPath
                        ? menuItem.getPath(
                            language
                          )
                        : menuItem.path;

                    const Icon =
                      menuItem.icon;

                    const active =
                      isActive(path);

                    const highlightStyle =
                      menuItem.highlight
                        ? HIGHLIGHT_STYLES[
                            menuItem
                              .highlight
                          ]
                        : null;

                    return (
                      <li
                        key={
                          menuItem.key
                        }
                      >
                        <button
                          type="button"
                          onClick={() =>
                            navigate(path)
                          }
                          className={[
                            `
                              flex w-full
                              items-center gap-3
                              rounded-lg
                              px-4 py-2.5
                              text-left
                              font-medium
                              transition-all
                              duration-200
                            `,

                            active
                              ? `
                                scale-[1.02]
                                bg-gradient-to-r
                                ${
                                  highlightStyle?.active ||
                                  "from-blue-600 to-blue-700"
                                }
                                text-white
                                shadow-lg
                              `
                              : `
                                text-gray-200
                                hover:bg-gray-700
                                hover:text-white
                                ${
                                  highlightStyle?.hover ||
                                  ""
                                }
                              `,
                          ].join(
                            " "
                          )}
                        >
                          <Icon
                            size={18}
                            className="shrink-0"
                          />

                          <span
                            className="
                              min-w-0
                              flex-1
                              truncate
                            "
                          >
                            {translate(
                              language,
                              menuItem.labelKey,
                              menuItem.fallback
                            )}
                          </span>

                          {active && (
                            <span
                              className="
                                h-1.5 w-1.5
                                shrink-0
                                animate-pulse
                                rounded-full
                                bg-white
                              "
                            />
                          )}
                        </button>
                      </li>
                    );
                  }
                )}
              </React.Fragment>
            );
          })}
        </ul>





      </nav>

      {/* ALT: AYARLAR VE ÇIKIŞ */}

      <div
        className="
          shrink-0 space-y-3
          border-t border-white/10
          bg-gray-900/95
          px-4 pt-3
          shadow-[0_-18px_45px_rgba(0,0,0,0.28)]
          backdrop-blur-xl
        "
        style={{
          paddingBottom:
            "calc(env(safe-area-inset-bottom) + 16px)",
        }}
      >
        {canSeeSettings && (
          <button
            type="button"
            onClick={() =>
              navigate(
                "/ayarlar"
              )
            }
            className={[
              `
                group relative
                flex w-full
                items-center
                justify-center
                gap-3
                overflow-hidden
                rounded-2xl
                py-3
                font-bold
                transition-all
                duration-300
                hover:-translate-y-0.5
                active:scale-[0.98]
              `,

              isActive(
                "/ayarlar"
              )
                ? `
                  bg-gradient-to-r
                  from-blue-600
                  to-blue-700
                  text-white
                  shadow-lg
                  shadow-blue-900/30
                `
                : `
                  border
                  border-white/10
                  bg-white/10
                  text-white
                  hover:bg-white/15
                `,
            ].join(" ")}
          >
            <span
              className="
                absolute inset-0
                bg-gradient-to-r
                from-white/10
                via-white/5
                to-transparent
                opacity-0
                transition-opacity
                duration-300
                group-hover:opacity-100
              "
            />

            <Settings
              size={20}
              className="
                relative z-10
                transition-transform
                duration-300
                group-hover:rotate-45
              "
            />

            <span
              className="
                relative z-10
                whitespace-nowrap
                text-[15px]
                sm:text-[16px]
              "
            >
              {translate(
                language,
                "settings",
                "Ayarlar"
              )}
            </span>

            {isActive(
              "/ayarlar"
            ) && (
              <span
                className="
                  absolute left-0 top-0
                  h-full w-1
                  bg-white
                "
              />
            )}
          </button>
        )}

        <button
          type="button"
          onClick={
            handleLogout
          }
          className="
            group relative
            flex w-full
            items-center
            justify-center
            gap-3
            overflow-hidden
            rounded-2xl
            bg-gradient-to-r
            from-red-600
            via-red-600
            to-red-700
            py-3.5
            text-[15px]
            font-extrabold
            text-white
            shadow-lg
            shadow-red-950/30
            transition-all
            duration-300
            hover:-translate-y-0.5
            hover:from-red-700
            hover:via-red-700
            hover:to-red-800
            hover:shadow-red-500/40
            active:scale-[0.98]
            sm:text-[17px]
          "
        >
          <span
            className="
              absolute inset-0
              bg-gradient-to-r
              from-white/15
              via-white/5
              to-transparent
              opacity-0
              transition-opacity
              duration-300
              group-hover:opacity-100
            "
          />

          <LogOut
            size={22}
            className="
              relative z-10
              transition-transform
              duration-300
              group-hover:translate-x-1
            "
          />

          <span
            className="
              relative z-10
              whitespace-nowrap
            "
          >
            {translate(
              language,
              "logout",
              "Çıkış"
            )}
          </span>
        </button>
      </div>
    </aside>
  );
}